import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Parses a job description text and extracts structured requirements.
 * @param {string} jobDescriptionText - Raw job description text from upload.
 * @returns {Promise<Object>} Structured job opportunity object.
 */
export async function parseJobDescription(jobDescriptionText) {
  try {
    const prompt = `You are an expert recruiter analyzing job descriptions. Extract key information from the following job description and structure it for candidate matching.

Job Description:
${jobDescriptionText}

Extract and structure the following information:
- Role title
- Company description (if mentioned, otherwise "Not specified")
- Location (city and province/state, or "Remote" if applicable)
- Work mode (Remote/Hybrid/On-site)
- Team size (if mentioned)
- Growth stage (Startup/Series A/Series B/Series C/Enterprise/etc., or "Not specified")
- Employee count (if mentioned)
- Compensation range (if mentioned)
- Key requirements (list of 4-8 essential qualifications)
- Benefits (list of benefits mentioned, or empty array)
- Required years of experience
- Required seniority level (Senior/Lead/Principal/Executive)
- Industry (Technology/Construction/Real Estate/Finance/Healthcare/Other)
- Key skills (list of 5-10 technical or domain skills)

Be thorough and extract as much relevant information as possible for accurate candidate matching.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert recruiter with deep experience in analyzing job descriptions and extracting structured requirements for candidate matching. Be thorough and accurate.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'job_description_analysis',
          schema: {
            type: 'object',
            properties: {
              roleTitle: { 
                type: 'string',
                description: 'The job title or role name'
              },
              companyDescription: { 
                type: 'string',
                description: 'Brief description of the company'
              },
              location: { 
                type: 'string',
                description: 'Job location (city, province/state) or Remote'
              },
              workMode: { 
                type: 'string',
                enum: ['Remote', 'Hybrid', 'On-site', 'Not specified'],
                description: 'Work arrangement type'
              },
              teamSize: { 
                type: 'string',
                description: 'Size of the team (e.g., "10-20 engineers")'
              },
              growthStage: { 
                type: 'string',
                description: 'Company growth stage or maturity'
              },
              employeeCount: { 
                type: 'string',
                description: 'Total number of employees'
              },
              compensationRange: { 
                type: 'string',
                description: 'Salary or compensation range'
              },
              requirements: { 
                type: 'array',
                items: { type: 'string' },
                description: 'List of key requirements and qualifications'
              },
              benefits: { 
                type: 'array',
                items: { type: 'string' },
                description: 'List of benefits and perks'
              },
              yearsOfExperience: { 
                type: 'string',
                description: 'Required years of experience (e.g., "10+", "8-12")'
              },
              seniorityLevel: { 
                type: 'string',
                enum: ['Senior', 'Lead', 'Principal', 'Executive', 'Not specified'],
                description: 'Required seniority level'
              },
              industry: { 
                type: 'string',
                description: 'Primary industry or sector'
              },
              keySkills: { 
                type: 'array',
                items: { type: 'string' },
                description: 'List of key technical or domain skills'
              }
            },
            required: ['roleTitle', 'companyDescription', 'location', 'workMode', 'requirements', 'benefits', 'industry', 'keySkills'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    const parsedJob = JSON.parse(response?.choices?.[0]?.message?.content);
    return parsedJob;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error parsing job description:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default {
  parseJobDescription
};